import { observer } from 'mobx-react-lite';
import type { SensorAlertBundle } from '@mark43/cad-api';
import {
    Box,
    Button,
    cssVar,
    Flex,
    IconButton,
    Menu,
    MenuContent,
    MenuItem,
    MenuTrigger,
    Tag,
    Text,
    Tooltip,
    useDisclosure,
} from '@cad-arc';
import {
    formatSensorAlertClipTime,
    formatSensorAlertMonthDayTime,
    formatSensorEventLocation,
    getSensorEventTags,
    getSensorEventTitle,
    SensorEventTag,
} from './helpers';
import { AttachToEventModal } from './attach-to-event-modal';
import { SensorEventClip } from './sensor-event-clip';
import { SensorSeverityDot } from './sensor-severity-dot';
import { useCreateEventFromAlert } from './use-create-event-from-alert';
import { useOpenSensorPanel } from './use-open-sensor-panel';
import { useOpenSensorEventPanel } from './use-open-sensor-event-panel';
import { useSensorPriority } from './use-sensor-priority';
import { useSensorsStore } from './use-sensors-store';

/*
 * Classifications (what was detected) are set apart from metadata with Arc's own warning accent on
 * Arc's Tag. Metadata tags are Arc's Tag as it comes.
 */
const CLASSIFICATION_TAG_STYLE = {
    '--arc-tag-default-bg': cssVar('arc.colors.warning.accent'),
    '--arc-tag-default-text': cssVar('arc.colors.warning.accentContent'),
} as React.CSSProperties;

/** Priority 1 in Arc's negative accent, matching the dot; any other priority is a plain tag. */
const PRIORITY_1_TAG_STYLE = {
    '--arc-tag-default-bg': cssVar('arc.colors.negative.accent'),
    '--arc-tag-default-text': cssVar('arc.colors.negative.accentContent'),
} as React.CSSProperties;

function SensorEventTagChip({ tag }: { tag: SensorEventTag }): React.ReactElement {
    return (
        <Tag
            size="sm"
            style={tag.kind === 'classification' ? CLASSIFICATION_TAG_STYLE : undefined}
        >
            {tag.label}
        </Tag>
    );
}

/**
 * The More menu — navigation only. Event actions are always visible on the card and never go in
 * here, per the design.
 */
function SensorEventMoreMenu({ alert }: { alert: SensorAlertBundle }): React.ReactElement {
    const openSensorEventPanel = useOpenSensorEventPanel();
    const openSensorPanel = useOpenSensorPanel();

    return (
        <Menu>
            <Tooltip content="More actions">
                <MenuTrigger asChild>
                    <IconButton variant="outline" icon="MoreActions" aria-label="More actions" />
                </MenuTrigger>
            </Tooltip>
            {/* Upward, anchored to the button: the menu opens over the card, not below it. */}
            <MenuContent side="top" align="end" style={{ minWidth: '230px' }}>
                <MenuItem
                    onClick={() => openSensorEventPanel({ alertKey: alert.alertKey })}
                >
                    Open sensor event
                </MenuItem>
                <MenuItem onClick={() => openSensorPanel({ sensorId: alert.sensorExternalId })}>
                    Open sensor
                </MenuItem>
            </MenuContent>
        </Menu>
    );
}

export type SensorEventCardProps = {
    alert: SensorAlertBundle;
    /**
     * `time` in the drawer, where the list is recent enough for the date to be implied;
     * `dateTime` in the event panel, where linked events can span days.
     */
    timestamp?: 'time' | 'dateTime';
    isSelected?: boolean;
    /** Omit for a card that cannot be selected, like the event panel's. */
    onSelect?: (alert: SensorAlertBundle) => void;
    /** What sits under the tags: the drawer's actions, or the event panel's panel links. */
    children?: React.ReactNode;
};

/**
 * The canonical Sensor Event Card, from the drawer design (option 6c): severity, title, time,
 * place, clip and tags. What goes underneath is the caller's — the drawer puts the event actions
 * there, the event panel's Sensor Events tab (design 7a) only links out to panels.
 *
 * **Linked state changes nothing on the card.** No chip, no badge, and both event actions stay
 * available so an event can be attached to several CAD events.
 */
export const SensorEventCard = observer(function SensorEventCard({
    alert,
    timestamp = 'time',
    isSelected = false,
    onSelect,
    children,
}: SensorEventCardProps): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const priority = useSensorPriority(alert);

    const title = getSensorEventTitle(alert);
    const location = formatSensorEventLocation(sensorsStore.getSensorForAlert(alert));
    const tags = getSensorEventTags(alert);
    const formattedTime =
        timestamp === 'dateTime'
            ? formatSensorAlertMonthDayTime(alert.createdDateUtc)
            : formatSensorAlertClipTime(alert.createdDateUtc);

    return (
        <Flex
            as="li"
            direction="column"
            gap="10px"
            padding="14px"
            listStyleType="none"
            onClick={onSelect ? () => onSelect(alert) : undefined}
            borderBottom="sm"
            borderColor="border.default"
            backgroundColor={isSelected ? 'selected.accent' : undefined}
            data-testid="SENSOR_EVENT_CARD"
            data-selected={isSelected || undefined}
        >
            <Flex direction="column" gap="4px" minWidth={0}>
                <Flex alignItems="center" gap="8px" minWidth={0}>
                    <SensorSeverityDot isPriority1={priority.isPriority1} />
                    <Box flex="1 1 auto" minWidth={0}>
                        <Text
                            fontSize="15px"
                            fontWeight="bold"
                            lineHeight="1.25"
                            color="primary"
                            isTruncated
                        >
                            {title}
                        </Text>
                    </Box>
                    {/* Never wraps: the box holds the line, the text only styles it. */}
                    <Box flexShrink={0} whiteSpace="nowrap">
                        <Text
                            fontFamily="mono"
                            fontSize="12px"
                            fontWeight="semibold"
                            lineHeight="none"
                            color="secondary"
                        >
                            {formattedTime ?? '--:--:--'}
                        </Text>
                    </Box>
                </Flex>
                {/* Past the dot, so it lines up with the title text. */}
                <Box paddingLeft="16px" minWidth={0}>
                    <Text fontSize="12px" lineHeight="1.4" color="tertiary" isTruncated title={location}>
                        {location ?? 'Unknown location'}
                    </Text>
                </Box>
            </Flex>

            <SensorEventClip imageUrl={alert.imageUrl} alt={title} />

            <Flex gap="5px" flexWrap="wrap">
                {/* Priority leads: it is what the dispatcher triages by. */}
                {priority.label ? (
                    <Tag
                        size="sm"
                        style={priority.isPriority1 ? PRIORITY_1_TAG_STYLE : undefined}
                        data-testid="SENSOR_EVENT_PRIORITY"
                    >
                        {priority.label}
                    </Tag>
                ) : undefined}
                {tags.map((tag) => (
                    <SensorEventTagChip key={tag.label} tag={tag} />
                ))}
            </Flex>

            {children ? (
                // Clicks on the card's controls should not also select the card.
                <Box onClick={(event) => event.stopPropagation()}>{children}</Box>
            ) : undefined}
        </Flex>
    );
});

/**
 * The drawer's action bar: the two event actions, archive, and the More menu. Create event and
 * Attach to event are the only event actions and are always visible, per the design.
 */
export const SensorEventCardActions = observer(function SensorEventCardActions({
    alert,
    isArchived,
    onArchive,
}: {
    alert: SensorAlertBundle;
    isArchived: boolean;
    /** Archive (or restore) this one event. The drawer passes the version with an undo toast. */
    onArchive: (alert: SensorAlertBundle, archived: boolean) => void;
}): React.ReactElement {
    const createEventFromAlert = useCreateEventFromAlert();
    const attachModal = useDisclosure();

    return (
        <>
            <Flex alignItems="center" gap="6px">
                <Box flex="1 1 0" minWidth={0}>
                    <Button variant="solid" width="100%" onClick={() => createEventFromAlert(alert)}>
                        Create event
                    </Button>
                </Box>
                {/* The ellipsis in the label says it opens a picker. */}
                <Box flex="1 1 0" minWidth={0}>
                    <Button variant="outline" width="100%" onClick={attachModal.onOpen}>
                        Attach to event…
                    </Button>
                </Box>
                <Tooltip content={isArchived ? 'Restore' : 'Archive'}>
                    <IconButton
                        variant="outline"
                        icon={isArchived ? 'Unarchive' : 'Archive'}
                        aria-label={isArchived ? 'Restore' : 'Archive'}
                        onClick={() => onArchive(alert, !isArchived)}
                    />
                </Tooltip>
                <SensorEventMoreMenu alert={alert} />
            </Flex>

            {/* Mounted only while open, so a list of cards does not carry a live autocomplete each. */}
            {attachModal.isOpen ? (
                <AttachToEventModal
                    alert={alert}
                    isOpen={attachModal.isOpen}
                    onClose={attachModal.onClose}
                />
            ) : undefined}
        </>
    );
});
